/**
 * CustomerAddressCaptureForm — capturing a new address against the customer
 * spine (customer-address-spine 03 §15). Saving creates a CIS-mastered
 * delivery address for the customer straight away, and hands it back so the
 * address book modal that holds the form can choose it. The form has no
 * dialog of its own: the modal supplies the title, so the picker never
 * stacks a second modal on the first.
 *
 * *Find by postcode* (order to invoice doc 102 §8) fills the address from
 * the premises at a postcode; when no lookup is set up the server says so
 * and the form stays usable by hand. The recipient and delivery
 * instructions travel with the address onto each delivery line (doc 102 §7).
 *
 * The server owns every field rule: it refuses with field errors (bound
 * next to the inputs here, per .claude/rules/api-validation.md), normalises
 * the postcode, and decides how many address lines are allowed. The
 * country is a picker of the ISO countries the server offers, defaulting to
 * GB — a free-text country could not be validated.
 */

import { useEffect, useId, useRef, useState } from "react";
import { Loader2, Plus, Search } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { ApiError } from "@shared-core/data/api/errors";
import type {
  AddressCountry,
  CustomerAddress,
  FoundAddress,
} from "@shared-core/data/api-types";
import {
  extractErrorDetailMessage,
  extractFieldErrors,
  type FieldError,
} from "@shared-core/data/validation";
import { splitAddressLines } from "@shared-core/domain/address-lines";
import { Button } from "../../primitives/button";

const DEFAULT_COUNTRY_CODE = "GB";

/**
 * What the dialog says when the lookup can't be reached at all (offline, a
 * dropped connection). Any answer from the server is shown in its own words,
 * the 503 of doc 102 §8 included.
 */
export const POSTCODE_LOOKUP_UNREACHABLE =
  "Postcode lookup didn't answer: type the address in.";

/** The inputs a captured address is typed into. */
export type CaptureAddressField =
  | "name"
  | "lines"
  | "town"
  | "postcode"
  | "country"
  | "recipient_name"
  | "recipient_phone"
  | "recipient_email"
  | "delivery_instructions";

export interface CaptureAddressDraft {
  name: string;
  /** One address line per typed line. */
  lines: string;
  town: string;
  postcode: string;
  countryCode: string;
  recipientName: string;
  recipientPhone: string;
  recipientEmail: string;
  deliveryInstructions: string;
}

const EMPTY_DRAFT: CaptureAddressDraft = {
  name: "",
  lines: "",
  town: "",
  postcode: "",
  countryCode: DEFAULT_COUNTRY_CODE,
  recipientName: "",
  recipientPhone: "",
  recipientEmail: "",
  deliveryInstructions: "",
};

/** Where *Find by postcode* has got to. */
type PostcodeLookup =
  | { kind: "idle" }
  | { kind: "searching" }
  | { kind: "found"; premises: FoundAddress[] }
  | { kind: "refused"; message: string };

/**
 * The server's field errors, by the input each belongs next to. A field the
 * form has no input for (e.g. `comment`) is left in `unbound` for the
 * banner, so nothing the server said goes unshown.
 */
export function bindCaptureAddressErrors(errors: readonly FieldError[]): {
  byField: Partial<Record<CaptureAddressField, string>>;
  unbound: string[];
} {
  const byField: Partial<Record<CaptureAddressField, string>> = {};
  const unbound: string[] = [];
  for (const error of errors) {
    const field = inputFor(error.field);
    if (field === null) {
      unbound.push(error.message);
      continue;
    }
    // Several `lines.N` errors share the one box.
    byField[field] = byField[field]
      ? `${byField[field]} ${error.message}`
      : error.message;
  }
  return { byField, unbound };
}

const DIRECT_FIELDS: ReadonlySet<string> = new Set<CaptureAddressField>([
  "name",
  "town",
  "postcode",
  "recipient_name",
  "recipient_phone",
  "recipient_email",
  "delivery_instructions",
]);

function inputFor(serverField: string): CaptureAddressField | null {
  if (serverField === "lines" || serverField.startsWith("lines."))
    return "lines";
  if (serverField === "country_code") return "country";
  if (DIRECT_FIELDS.has(serverField)) return serverField as CaptureAddressField;
  return null;
}

/** The server's own account of a non-field refusal (a closed customer's 409, a 403). */
function refusalMessage(err: unknown): string | null {
  if (!(err instanceof ApiError) || err.status >= 500) return null;
  const detail = (err.body as { detail?: unknown } | null)?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  return extractErrorDetailMessage(err);
}

/** Why *Find by postcode* found nothing to choose from, in the server's words where it gave some. */
export function postcodeLookupRefusal(err: unknown): string {
  const fieldError = extractFieldErrors(err)[0];
  if (fieldError) return fieldError.message;
  if (err instanceof ApiError) {
    const detail = (err.body as { detail?: unknown } | null)?.detail;
    if (typeof detail === "string" && detail.trim()) return detail;
  }
  return POSTCODE_LOOKUP_UNREACHABLE;
}

/** The draft with a found premise's address in it; the name only when the premise has an organisation and none is typed. */
export function fillFromPremise(
  draft: CaptureAddressDraft,
  premise: FoundAddress,
): CaptureAddressDraft {
  return {
    ...draft,
    name: draft.name.trim() ? draft.name : (premise.organisation ?? ""),
    lines: premise.lines.join("\n"),
    town: premise.town ?? "",
    postcode: premise.postcode,
    countryCode: DEFAULT_COUNTRY_CODE,
  };
}

type Failure =
  | { kind: "fields"; count: number; unbound: string[] }
  | { kind: "message"; message: string };

export interface CustomerAddressCaptureFormProps {
  /** The spine customer the address is created under (e.g. `CUS-K48193`). */
  cisCustomerCode: string;
  /** Called with the created address; the caller moves on from the form. */
  onCreated: (address: CustomerAddress) => void;
  /** Leaves the form without saving: back to the list of addresses. */
  onBack: () => void;
  disabled?: boolean;
}

export function CustomerAddressCaptureForm({
  cisCustomerCode,
  onCreated,
  onBack,
  disabled = false,
}: CustomerAddressCaptureFormProps) {
  const adapter = useDataAdapter();
  const idPrefix = useId();
  const [draft, setDraft] = useState<CaptureAddressDraft>(EMPTY_DRAFT);
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<CaptureAddressField, string>>
  >({});
  const [failure, setFailure] = useState<Failure | null>(null);
  const [saving, setSaving] = useState(false);
  const [countries, setCountries] = useState<AddressCountry[] | null>(null);
  const [countriesFailed, setCountriesFailed] = useState(false);
  const [lookupPostcode, setLookupPostcode] = useState("");
  const [lookup, setLookup] = useState<PostcodeLookup>({ kind: "idle" });

  const formRef = useRef<HTMLFormElement>(null);

  // The form is mounted fresh each time the modal shows it: start on the
  // postcode lookup, the quickest way to an address.
  useEffect(() => {
    formRef.current?.querySelector("input")?.focus();
  }, []);

  useEffect(() => {
    let cancelled = false;
    setCountriesFailed(false);
    adapter
      .listAddressCountries()
      .then((list) => {
        if (!cancelled) setCountries(list);
      })
      .catch(() => {
        if (!cancelled) setCountriesFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [adapter]);

  const busy = disabled || saving;
  const canSave =
    !busy &&
    draft.name.trim() !== "" &&
    draft.lines.trim() !== "" &&
    draft.postcode.trim() !== "";
  const canFind =
    !busy && lookup.kind !== "searching" && lookupPostcode.trim() !== "";

  const findAddress = async () => {
    if (!canFind) return;
    const postcode = lookupPostcode.trim();
    setLookup({ kind: "searching" });
    try {
      const premises = await adapter.findAddressesByPostcode(
        cisCustomerCode,
        postcode,
      );
      setLookup(
        premises.length > 0
          ? { kind: "found", premises }
          : {
              kind: "refused",
              message: `Nothing found at ${postcode.toUpperCase()}: type the address in.`,
            },
      );
      if (premises.length === 1)
        setDraft((d) => fillFromPremise(d, premises[0]));
    } catch (err) {
      setLookup({ kind: "refused", message: postcodeLookupRefusal(err) });
    }
  };

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setFieldErrors({});
    setFailure(null);
    try {
      const created = await adapter.createCustomerAddress(cisCustomerCode, {
        is_delivery: true,
        is_invoice: false,
        is_default_delivery: false,
        name: draft.name,
        lines: splitAddressLines(draft.lines),
        town: draft.town.trim() || null,
        county: null,
        postcode: draft.postcode,
        country_code: draft.countryCode,
        recipient_name: draft.recipientName.trim() || null,
        recipient_phone: draft.recipientPhone.trim() || null,
        recipient_email: draft.recipientEmail.trim() || null,
        delivery_instructions: draft.deliveryInstructions.trim() || null,
        comment: null,
      });
      onCreated(created);
    } catch (err) {
      const errors = extractFieldErrors(err);
      if (errors.length > 0) {
        const { byField, unbound } = bindCaptureAddressErrors(errors);
        setFieldErrors(byField);
        setFailure({ kind: "fields", count: errors.length, unbound });
      } else {
        setFailure({
          kind: "message",
          message:
            refusalMessage(err) ??
            "This address could not be saved — please try again.",
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const inputClass = (hasError: boolean) =>
    `w-full px-2 py-1 text-sm border rounded bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary ${
      hasError ? "border-[color:var(--color-error,red)]" : "border-border"
    }`;
  const fieldProps = (field: CaptureAddressField) => {
    const error = fieldErrors[field];
    return {
      id: `${idPrefix}-${field}`,
      "aria-invalid": error ? true : undefined,
      "aria-describedby": error ? `${idPrefix}-${field}-error` : undefined,
      disabled: busy,
      className: inputClass(Boolean(error)),
    };
  };
  const errorFor = (field: CaptureAddressField) =>
    fieldErrors[field] ? (
      <p
        id={`${idPrefix}-${field}-error`}
        className="text-xs text-[color:var(--color-error,red)]"
      >
        {fieldErrors[field]}
      </p>
    ) : null;
  const label = (htmlFor: string, text: string) => (
    <label
      htmlFor={`${idPrefix}-${htmlFor}`}
      className="block text-xs font-medium"
    >
      {text}
    </label>
  );
  const textField = (
    field: CaptureAddressField,
    text: string,
    value: string,
    onChange: (value: string) => void,
    type: "text" | "tel" | "email" = "text",
  ) => (
    <div className="space-y-1">
      {label(field, text)}
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        {...fieldProps(field)}
      />
      {errorFor(field)}
    </div>
  );
  const lookupRefusal = lookup.kind === "refused" ? lookup.message : null;

  return (
    <form
      ref={formRef}
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      {failure && (
        <div
          role="alert"
          className="rounded-md border border-[color:var(--color-error,red)]/30 bg-[color:var(--color-error,red)]/5 px-3 py-2 text-xs text-[color:var(--color-error,red)]"
        >
          {failure.kind === "message" ? (
            failure.message
          ) : (
            <>
              {failure.count === 1
                ? "1 field needs attention"
                : `${failure.count} fields need attention`}{" "}
              before this address can be saved.
              {failure.unbound.map((message) => (
                <span key={message} className="block mt-1">
                  {message}
                </span>
              ))}
            </>
          )}
        </div>
      )}
      <div className="space-y-1">
        {label("lookup", "Find by postcode")}
        <div className="flex gap-2">
          <input
            type="text"
            id={`${idPrefix}-lookup`}
            value={lookupPostcode}
            onChange={(e) => setLookupPostcode(e.target.value)}
            onKeyDown={(e) => {
              // Enter finds the address rather than saving the form.
              if (e.key !== "Enter") return;
              e.preventDefault();
              void findAddress();
            }}
            aria-describedby={
              lookupRefusal ? `${idPrefix}-lookup-refusal` : undefined
            }
            disabled={busy}
            className={inputClass(false)}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void findAddress()}
            disabled={!canFind}
          >
            {lookup.kind === "searching" ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <Search className="size-3.5" />
            )}
            Find address
          </Button>
        </div>
        {lookupRefusal && (
          <p
            id={`${idPrefix}-lookup-refusal`}
            role="status"
            className="text-xs text-muted-foreground"
          >
            {lookupRefusal}
          </p>
        )}
      </div>
      {lookup.kind === "found" && lookup.premises.length > 1 && (
        <div className="space-y-1">
          {label("premise", "Choose the address")}
          <select
            id={`${idPrefix}-premise`}
            defaultValue=""
            onChange={(e) => {
              const premise = lookup.premises[Number(e.target.value)];
              if (premise) setDraft((d) => fillFromPremise(d, premise));
            }}
            disabled={busy}
            className={inputClass(false)}
          >
            <option value="" disabled>
              {`${lookup.premises.length} addresses`}
            </option>
            {lookup.premises.map((premise, index) => (
              <option key={index} value={index}>
                {premise.label}
              </option>
            ))}
          </select>
        </div>
      )}
      <div className="space-y-1">
        {label("name", "Name")}
        <input
          type="text"
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          placeholder="e.g. Bramble Foods — Bristol Warehouse"
          {...fieldProps("name")}
        />
        {errorFor("name")}
      </div>
      <div className="space-y-1">
        {label("lines", "Address")}
        <textarea
          rows={3}
          value={draft.lines}
          onChange={(e) => setDraft({ ...draft, lines: e.target.value })}
          placeholder={"One line per line, e.g.\nUnit 4\nRiverside Way"}
          {...fieldProps("lines")}
        />
        {errorFor("lines")}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {textField("town", "Town / city", draft.town, (town) =>
          setDraft({ ...draft, town }),
        )}
        {textField("postcode", "Postcode", draft.postcode, (postcode) =>
          setDraft({ ...draft, postcode }),
        )}
      </div>
      <div className="space-y-1">
        {label("country", "Country")}
        <select
          value={draft.countryCode}
          onChange={(e) => setDraft({ ...draft, countryCode: e.target.value })}
          {...fieldProps("country")}
        >
          {countries === null ? (
            // The default stays chosen while the list loads (or if it
            // cannot), so the form never sends an empty country.
            <option value={DEFAULT_COUNTRY_CODE}>
              {countriesFailed ? DEFAULT_COUNTRY_CODE : "Loading countries…"}
            </option>
          ) : (
            countries.map((country) => (
              <option key={country.code} value={country.code}>
                {country.name}
              </option>
            ))
          )}
        </select>
        {countriesFailed && (
          <p className="text-xs text-muted-foreground">
            The country list could not be loaded — the address will be saved in{" "}
            {DEFAULT_COUNTRY_CODE}.
          </p>
        )}
        {errorFor("country")}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {textField(
          "recipient_name",
          "Recipient",
          draft.recipientName,
          (recipientName) => setDraft({ ...draft, recipientName }),
        )}
        {textField(
          "recipient_phone",
          "Phone",
          draft.recipientPhone,
          (recipientPhone) => setDraft({ ...draft, recipientPhone }),
          "tel",
        )}
        {textField(
          "recipient_email",
          "Email",
          draft.recipientEmail,
          (recipientEmail) => setDraft({ ...draft, recipientEmail }),
          "email",
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        Phone needed for timed and pallet deliveries.
      </p>
      {textField(
        "delivery_instructions",
        "Delivery instructions",
        draft.deliveryInstructions,
        (deliveryInstructions) => setDraft({ ...draft, deliveryInstructions }),
      )}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <button
          type="button"
          onClick={onBack}
          disabled={saving}
          className="text-xs text-muted-foreground hover:underline px-2"
        >
          Back to addresses
        </button>
        <button
          type="submit"
          disabled={!canSave}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            <Plus className="size-3.5" />
          )}
          {saving ? "Saving address…" : "Save address"}
        </button>
      </div>
    </form>
  );
}
